import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '../ui/Modal.jsx';
import Button from '../ui/Button.jsx';
import OtterFigure from '../otter/OtterFigure.jsx';
import { getMoment, partLabel } from '../../constants/otterMoments';

/**
 * Sheet "Neu im Schrank" (Aura v2, Okt 2026): ein neues Teil als Rueckmeldung.
 *
 * - Ein Teil: Figur mit genau diesem Teil, Name, Rueckmeldesatz, zwei gleich grosse
 *   Knoepfe "Anziehen" / "Spaeter". Nichts wird vorab angezogen.
 * - Mehrere Teile: Sammel-Sheet mit Liste, "Anziehen" je Teil, "Fertig".
 * - Moment ohne Teil (erster_schritt): nur der Satz und "Okay".
 * - Erstes Teil ueberhaupt und noch nie gefragt (`askVisibility`): danach im selben
 *   Sheet die Frage, ob andere den Otter mit Teilen sehen duerfen. Zwei gleich
 *   grosse, gleich gewichtete Knoepfe, keine Vorauswahl.
 *
 * Konflikte (zwei Teile, die nicht zusammen gehen) loest `onWear`; der Hinweis
 * steht kurz im Sheet, kein Toast. Schliessen markiert die Teile als gesehen
 * (das erledigt `onClose` im Hook useUnlocks).
 */
const TWO_BUTTONS = 'grid w-full grid-cols-2 gap-3';

const UnlockSheet = ({
  open, items, level, outfit, askVisibility, onWear, onVisibility, onClose,
}) => {
  const [step, setStep] = useState('items');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [wornOnce, setWornOnce] = useState(false);

  if (!open || !Array.isArray(items) || items.length === 0) return null;

  const wornKeys = new Set(Object.values(outfit || {}));
  const single = items.length === 1 ? items[0] : null;

  const finishItems = () => {
    setNote('');
    setError('');
    if (askVisibility) setStep('visibility');
    else onClose();
  };

  const handleWear = async (key, { advance = false } = {}) => {
    setBusy(key);
    setError('');
    setNote('');
    const result = (await onWear(key)) || {};
    setBusy(null);
    if (!result.ok) {
      setError(result.error || 'Das hat gerade nicht geklappt.');
      return;
    }
    setWornOnce(true);
    if (result.note) {
      setNote(result.note);
      return;
    }
    if (advance) finishItems();
  };

  const handleVisibility = async (visible) => {
    setBusy(visible ? 'ja' : 'nein');
    setError('');
    const ok = await onVisibility(visible);
    setBusy(null);
    if (ok) onClose();
    else setError('Das hat gerade nicht geklappt. Du kannst es auch später im Schrank einstellen.');
  };

  const handleModalClose = () => {
    if (step === 'items') finishItems();
    else onClose();
  };

  const feedbackOf = (item) => getMoment(item.key)?.feedback || '';
  const messages = (
    <div aria-live="polite" className="min-h-0">
      {note && <p className="mt-3 text-sm text-foreground" data-testid="unlock-note">{note}</p>}
      {error && <p className="mt-3 text-sm font-medium text-danger" role="alert">{error}</p>}
    </div>
  );

  let body;
  let footer;

  if (step === 'visibility') {
    body = (
      <div className="flex flex-col items-center text-center">
        <OtterFigure level={level} size={120} className="mb-3" />
        <h2 className="text-xl font-bold text-foreground">Dürfen andere deinen Otter mit Teilen sehen?</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Sonst sehen andere deinen Otter nicht. Du kannst das jederzeit im Schrank ändern.
        </p>
        {messages}
      </div>
    );
    footer = (
      <div className={TWO_BUTTONS}>
        <Button variant="secondary" fullWidth disabled={Boolean(busy)} onClick={() => handleVisibility(true)}>
          Ja, zeigen
        </Button>
        <Button variant="secondary" fullWidth disabled={Boolean(busy)} onClick={() => handleVisibility(false)}>
          Nein
        </Button>
      </div>
    );
  } else if (single && !single.accessory) {
    // Moment ohne Teil: nur der Satz
    body = (
      <div className="flex flex-col items-center text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">{getMoment(single.key)?.name || 'Neu'}</p>
        <p className="mt-3 text-base text-foreground">{feedbackOf(single)}</p>
      </div>
    );
    footer = <Button variant="primary" fullWidth onClick={finishItems}>Okay</Button>;
  } else if (single) {
    const label = partLabel(single);
    const isWorn = wornKeys.has(single.key);
    body = (
      <div className="flex flex-col items-center text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">Neu im Schrank</p>
        <OtterFigure
          level={level}
          parts={[single.accessory]}
          size={160}
          title={`Dein Otter mit ${label}`}
          className="my-3 animate-pop-in motion-reduce:animate-none"
        />
        {/* Bungee wie beim Level-Up: einer der wenigen lauten Momente (CD Regel 4) */}
        <h2 className="font-display text-2xl uppercase leading-none tracking-[0.04em] text-foreground">{label}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{feedbackOf(single)}</p>
        {messages}
      </div>
    );
    footer = wornOnce && note ? (
      <Button variant="primary" fullWidth onClick={finishItems}>Weiter</Button>
    ) : (
      <div className={TWO_BUTTONS}>
        <Button variant="secondary" fullWidth disabled={Boolean(busy)} onClick={finishItems}>
          Später
        </Button>
        <Button
          variant="primary"
          fullWidth
          disabled={Boolean(busy) || isWorn}
          onClick={() => handleWear(single.key, { advance: true })}
        >
          Anziehen
        </Button>
      </div>
    );
  } else {
    // Sammel-Sheet
    body = (
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">Neu im Schrank</p>
        <h2 className="mt-1 text-xl font-bold text-foreground">{`${items.length} neue Momente in deinem Schrank`}</h2>
        <ul className="mt-3 divide-y divide-border" aria-label="Neue Teile">
          {items.map((item) => {
            const label = item.accessory ? partLabel(item) : (getMoment(item.key)?.name || item.key);
            const isWorn = wornKeys.has(item.key);
            return (
              <li key={item.key} className="flex items-center gap-3 py-3" data-unlock-key={item.key}>
                {item.accessory ? (
                  <OtterFigure parts={[item.accessory]} size={56} title={`Otter mit ${label}`} className="shrink-0" />
                ) : (
                  <span className="h-14 w-14 shrink-0 rounded-full border-2 border-dashed border-border" aria-hidden="true" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground">{feedbackOf(item)}</p>
                </div>
                {item.accessory && (
                  <Button
                    variant={isWorn ? 'ghost' : 'secondary'}
                    size="sm"
                    className="min-h-[44px] shrink-0"
                    disabled={Boolean(busy) || isWorn}
                    aria-label={isWorn ? `${label} angezogen` : `${label} anziehen`}
                    onClick={() => handleWear(item.key)}
                  >
                    {isWorn ? 'Angezogen' : 'Anziehen'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
        {messages}
      </div>
    );
    footer = <Button variant="primary" fullWidth onClick={finishItems}>Fertig</Button>;
  }

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      ariaLabel={step === 'visibility' ? 'Sichtbarkeit deines Otters' : 'Neu im Schrank'}
      size="sm"
      hideClose
      bodyClassName="p-6"
      footer={footer}
    >
      {body}
    </Modal>
  );
};

const itemShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  accessory: PropTypes.string,
  slot: PropTypes.string,
  label: PropTypes.string,
});

UnlockSheet.propTypes = {
  open: PropTypes.bool,
  items: PropTypes.arrayOf(itemShape),
  level: PropTypes.number,
  outfit: PropTypes.object,
  askVisibility: PropTypes.bool,
  onWear: PropTypes.func.isRequired,
  onVisibility: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UnlockSheet;
